// Черновата на листа „Прехвърляне" — общото `useWorksheetDraft` със снимката
// на прехвърлянето: флагът „незапазени промени", autosave-ът (браузър +
// сървър) при ново прехвърляне, черновата при отваряне (зарежда я или пита) и
// нещата за футъра („Изтрий черновата" · „Запази временно"). Листът подава
// състоянието си лениво (`sheet`) — сумите идват от hook-а по-долу в тялото.
import dayjs, { type Dayjs } from "dayjs";
import type { Product } from "../../../../api/inventory";
import { useAppSelector } from "../../../../store/hooks";
import type { DraftRouterState } from "../../components/drafts/draftModel";
import type { DraftFooterProps } from "../../components/drafts/DraftWorksheetParts";
import { useWorksheetDraft } from "../../components/drafts/useWorksheetDraft";
import { transferPreviewItems } from "./useTransferLeave";
import {
  TRANSFER_DRAFT_STORE,
  hasTransferDraftContent,
  transferDraftSummary,
  type TransferDraft,
} from "./transferDraft";
import type { TransferLineDraft } from "./transferWorksheetShared";

// Състоянието на листа в момента на снимката
type SheetState = {
  date: Dayjs | null;
  fromWarehouseId: number | null;
  toWarehouseId: number | null;
  note: string;
  lines: TransferLineDraft[];
  totalGross: number;
};

type Params = {
  mode: "new" | "edit";
  homePath: string;
  routerState: DraftRouterState | null;
  products: Product[];
  warehouseNameOf: (warehouseId: number | null) => string | null;
  sheet: () => SheetState;
  // зареди черновата в листа (без `touch`)
  apply: (payload: TransferDraft) => void;
  onLoadError: () => void;
};

export const useTransferDraft = ({
  mode,
  homePath,
  routerState,
  products,
  warehouseNameOf,
  sheet,
  apply,
  onLoadError,
}: Params) => {
  const authNames = useAppSelector((s) => s.auth.names);
  const summaryOf = (state: SheetState, total: number | null) =>
    transferDraftSummary({
      fromName: warehouseNameOf(state.fromWarehouseId),
      toName: warehouseNameOf(state.toWarehouseId),
      linesCount: state.lines.length,
      total,
    });
  const worksheetDraft = useWorksheetDraft<TransferDraft>({
    type: "transfer",
    mode,
    homePath,
    routerState,
    local: TRANSFER_DRAFT_STORE,
    snapshot: () => {
      const state = sheet();
      return {
        payload: {
          v: 1,
          savedAt: new Date().toISOString(),
          savedBy: authNames || null,
          date: (state.date ?? dayjs()).toISOString(),
          fromWarehouseId: state.fromWarehouseId,
          toWarehouseId: state.toWarehouseId,
          note: state.note,
          lines: state.lines,
        },
        summary: summaryOf(state, state.totalGross),
      };
    },
    resume: { hasContent: hasTransferDraftContent, apply, onLoadError },
  });

  // „Изтрий черновата" · „Запази временно" за футъра — вика се в рендера на
  // футъра, когато сумите вече са сметнати
  const footer = (): DraftFooterProps => ({
    canDeleteDraft: worksheetDraft.sync.draftId != null || sheet().lines.length > 0,
    describeDraft: () => ({
      label: summaryOf(sheet(), null).label,
      items: transferPreviewItems(sheet().lines, products),
    }),
    onDeleteDraft: worksheetDraft.deleteDraft,
    onSaveTemp: worksheetDraft.saveTemp,
  });

  // „Продукт — количество" на намерената при отварянето чернова (въпросът)
  const pendingItems = transferPreviewItems(worksheetDraft.resume.pending?.payload.lines ?? [], products);

  return { ...worksheetDraft, footer, pendingItems };
};
